<template>
    <div class="campaign-items-progress">
        <h3 class="section-title mb-4">
            <v-icon color="primary" class="mr-2">mdi-format-list-checks</v-icon>
            Fundraising Breakdown
        </h3>

        <div v-if="!items || items.length === 0" class="text-center py-4 grey--text">
            No itemized goals for this campaign.
        </div>

        <div v-else class="items-list">
            <div
                v-for="item in items"
                :key="item.id"
                class="item-progress mb-4"
            >
                <div class="item-header d-flex align-center justify-space-between mb-2">
                    <div class="d-flex align-center">
                        <v-icon small class="mr-2" :color="getProgressColor(item)">
                            {{ getCategoryIcon(item.category) }}
                        </v-icon>
                        <span class="item-name">{{ item.name }}</span>
                        <v-chip
                            v-if="isFullyFunded(item)"
                            x-small
                            color="success"
                            text-color="white"
                            class="ml-2"
                        >
                            <v-icon x-small left>mdi-check</v-icon>
                            Funded
                        </v-chip>
                    </div>
                    <span class="item-amounts">
                        ${{ formatAmount(item.raised_amount) }} / ${{ formatAmount(item.goal_amount) }}
                    </span>
                </div>

                <v-progress-linear
                    :value="getProgressPercent(item)"
                    :color="getProgressColor(item)"
                    rounded
                    height="12"
                    background-color="#e9ecef"
                >
                </v-progress-linear>

                <div class="item-percent text-right mt-1">
                    <span :class="{ 'success--text': isFullyFunded(item) }">
                        {{ Math.ceil(getProgressPercent(item)) }}%
                    </span>
                </div>
            </div>
        </div>

        <v-card
            v-if="items && items.length > 0"
            elevation="0"
            class="totals-card pa-4 mt-4"
        >
            <v-row no-gutters>
                <v-col cols="6">
                    <div class="total-label">Total Goal</div>
                    <div class="total-value">${{ formatAmount(totalGoal) }}</div>
                </v-col>
                <v-col cols="6" class="text-right">
                    <div class="total-label">Total Raised</div>
                    <div class="total-value success--text">${{ formatAmount(totalRaised) }}</div>
                </v-col>
            </v-row>
        </v-card>
    </div>
</template>

<script>
const CATEGORY_ICONS = {
    tuition: 'mdi-school',
    books: 'mdi-book-open-variant',
    rent: 'mdi-home',
    furniture: 'mdi-sofa',
    gas: 'mdi-gas-station',
    food: 'mdi-food',
    custom: 'mdi-pencil',
    other: 'mdi-dots-horizontal'
}

export default {
    name: 'CampaignItemsProgress',
    props: {
        items: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        totalGoal() {
            if (!this.items) return 0
            return this.items.reduce((sum, item) => sum + (parseFloat(item.goal_amount) || 0), 0)
        },
        totalRaised() {
            if (!this.items) return 0
            return this.items.reduce((sum, item) => sum + (parseFloat(item.raised_amount) || 0), 0)
        }
    },
    methods: {
        getCategoryIcon(category) {
            return CATEGORY_ICONS[category] || CATEGORY_ICONS.other
        },
        getProgressPercent(item) {
            if (!item.goal_amount || item.goal_amount === 0) return 0
            const raised = parseFloat(item.raised_amount) || 0
            const goal = parseFloat(item.goal_amount)
            return Math.min((raised / goal) * 100, 100)
        },
        getProgressColor(item) {
            const percent = this.getProgressPercent(item)
            if (percent >= 100) return 'success'
            if (percent >= 50) return 'primary'
            return 'primary lighten-2'
        },
        isFullyFunded(item) {
            return this.getProgressPercent(item) >= 100
        },
        formatAmount(amount) {
            return (parseFloat(amount) || 0).toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            })
        }
    }
}
</script>

<style scoped>
.section-title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    display: flex;
    align-items: center;
}

.item-progress {
    padding: 12px 16px;
    background: #fafbfc;
    border-radius: 10px;
    border: 1px solid #e9ecef;
}

.item-name {
    font-size: 15px;
    font-weight: 500;
    color: #333;
}

.item-amounts {
    font-size: 14px;
    color: #666;
    font-weight: 500;
}

.item-percent {
    font-size: 12px;
    font-weight: 600;
    color: #666;
}

.totals-card {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
    border-radius: 10px !important;
}

.total-label {
    font-size: 12px;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
}

.total-value {
    font-size: 20px;
    font-weight: 700;
    color: #333;
}
</style>
